<script setup lang="ts">
/**
 * ErrorsView — `/errors` route (Story 307).
 *
 * Renders five trigger cards, one per library error variant
 * (`AuthError`, `RateLimitError`, `TimeoutError`, `ConfigError`,
 * `ConnectionError`). Clicking "Trigger" fabricates the error and
 * displays its `code`, `message`, and `retryable` fields plus any
 * subclass-specific metadata (`retryAfterMs`, `timeoutMs`, `field`,
 * `expected`, `validationErrors`).
 *
 * The rate-limit card additionally exposes a "Retry now" button that
 * simulates `withBackoff` (Story 205) honouring the `retryAfterMs`.
 *
 * Per AppSec, error messages are routed through the shared library's
 * `maskSecrets` helper before reaching the DOM. The trigger module
 * applies the mask up-front so the view itself does no scrubbing.
 */
import { reactive, ref } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import Card from '../components/Card.vue';
import AppAlert from '../components/AppAlert.vue';
import {
  errorTriggers,
  simulateRateLimitRecovery,
  type ErrorTrigger,
  type ErrorVariant,
} from '../services/errorTriggers';
import {
  ConnectionError,
  RateLimitError,
  ConfigError,
  TimeoutError,
} from '@gl-demo-ultimate-odupre/third-party-connections-shared';

interface FiredEntry {
  readonly trigger: ErrorTrigger;
  readonly error: ConnectionError;
  readonly firedAt: number;
}

const fired = reactive<Record<ErrorVariant, FiredEntry | null>>({
  auth: null,
  'rate-limit': null,
  timeout: null,
  config: null,
  connection: null,
});

const recovery = ref<{ id: ErrorVariant; attempts: number; retryAfterMs: number } | null>(null);
const recovering = ref(false);

const props = defineProps<{
  /** Tests inject a fast setTimeout to keep the recovery demo deterministic. */
  setTimeoutImpl?: typeof setTimeout;
  /** Tests can speed up the recovery simulation. */
  speedupFactor?: number;
}>();

function trigger(t: ErrorTrigger): void {
  fired[t.id] = {
    trigger: t,
    error: t.fire(),
    firedAt: Date.now(),
  };
  if (recovery.value && recovery.value.id === t.id) {
    recovery.value = null;
  }
}

async function retryRateLimit(): Promise<void> {
  const entry = fired['rate-limit'];
  if (!entry || !(entry.error instanceof RateLimitError)) return;
  recovering.value = true;
  try {
    const result = await simulateRateLimitRecovery(entry.error, {
      setTimeoutImpl: props.setTimeoutImpl,
      speedupFactor: props.speedupFactor ?? 1,
    });
    recovery.value = {
      id: 'rate-limit',
      attempts: result.attempts,
      retryAfterMs: result.retryAfterMs,
    };
  } finally {
    recovering.value = false;
  }
}

function variantToBanner(_t: ErrorTrigger): 'info' | 'success' | 'warning' | 'danger' {
  // Every error is a "danger" surface — the recovery banner is success.
  return 'danger';
}

function metadataEntries(err: ConnectionError): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  if (err instanceof RateLimitError) {
    out.push(['retryAfterMs', String(err.retryAfterMs)]);
  }
  if (err instanceof TimeoutError) {
    out.push(['timeoutMs', String(err.timeoutMs)]);
  }
  if (err instanceof ConfigError) {
    if (err.field) out.push(['field', err.field]);
    if (err.expected) out.push(['expected', err.expected]);
    if (err.validationErrors.length > 0) {
      out.push(['validationErrors', err.validationErrors.join(', ')]);
    }
  }
  return out;
}
</script>

<template>
  <div class="cwc-errors">
    <PageHeader
      title="Error states"
      subtitle="Trigger every error type the library can throw and inspect the rendered Pajamas surface (Story 307)."
    />
    <AppAlert variant="info" title="Mocked errors">
      Every trigger fabricates a deterministic library error — no real
      network calls are made. Use this page as the canonical visual QA
      reference for error UX across the rest of the app.
    </AppAlert>

    <ul class="cwc-errors__grid" data-testid="errors-grid">
      <li
        v-for="t in errorTriggers"
        :key="t.id"
        class="cwc-errors__item"
        :data-testid="`error-card-${t.id}`"
      >
        <Card :title="t.label">
          <p class="cwc-errors__description">{{ t.description }}</p>
          <button
            type="button"
            class="cwc-errors__trigger"
            :data-testid="`error-trigger-${t.id}`"
            @click="trigger(t)"
          >
            Trigger
          </button>
          <AppAlert
            v-if="fired[t.id]"
            :variant="variantToBanner(t)"
            :title="(fired[t.id] as FiredEntry).error.code"
            :data-testid="`error-banner-${t.id}`"
          >
            <p class="cwc-errors__message" :data-testid="`error-message-${t.id}`">
              {{ (fired[t.id] as FiredEntry).error.message }}
            </p>
            <dl class="cwc-errors__meta">
              <div class="cwc-errors__meta-row">
                <dt>retryable</dt>
                <dd :data-testid="`error-retryable-${t.id}`">
                  {{ (fired[t.id] as FiredEntry).error.retryable ? 'yes' : 'no' }}
                </dd>
              </div>
              <div class="cwc-errors__meta-row">
                <dt>provider</dt>
                <dd>{{ (fired[t.id] as FiredEntry).error.provider }}</dd>
              </div>
              <div
                v-for="[key, value] in metadataEntries((fired[t.id] as FiredEntry).error)"
                :key="key"
                class="cwc-errors__meta-row"
              >
                <dt>{{ key }}</dt>
                <dd :data-testid="`error-meta-${t.id}-${key}`">{{ value }}</dd>
              </div>
            </dl>
          </AppAlert>
          <div
            v-if="t.id === 'rate-limit' && fired['rate-limit']"
            class="cwc-errors__retry"
            data-testid="error-retry-controls"
          >
            <button
              type="button"
              class="cwc-errors__retry-btn"
              data-testid="error-retry-rate-limit"
              :disabled="recovering"
              @click="retryRateLimit"
            >
              <span v-if="recovering">Retrying…</span>
              <span v-else>Retry now (simulate withBackoff)</span>
            </button>
            <AppAlert
              v-if="recovery"
              variant="success"
              title="Recovered"
              data-testid="error-retry-success"
            >
              <p>
                <strong>{{ recovery.attempts }}</strong>
                attempt(s) succeeded after waiting
                <strong>{{ recovery.retryAfterMs }} ms</strong>.
              </p>
            </AppAlert>
          </div>
        </Card>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.cwc-errors {
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-4);
}

.cwc-errors__grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--gl-spacing-4);
}

.cwc-errors__description {
  margin: 0 0 var(--gl-spacing-2);
  font-size: var(--gl-font-size-sm);
  color: var(--gl-fg-subtle);
}

.cwc-errors__trigger {
  appearance: none;
  background: var(--gl-status-danger-bg);
  color: var(--gl-status-danger-fg);
  border: var(--gl-border-width) solid var(--gl-status-danger-fg);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-4);
  cursor: pointer;
  font: inherit;
  margin-bottom: var(--gl-spacing-3);
}

.cwc-errors__trigger:hover {
  filter: brightness(0.95);
}

.cwc-errors__message {
  margin: 0 0 var(--gl-spacing-2);
  font-family: var(--gl-font-family-mono);
  font-size: var(--gl-font-size-sm);
}

.cwc-errors__meta {
  margin: 0;
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: var(--gl-spacing-3);
  row-gap: var(--gl-spacing-1);
  font-size: var(--gl-font-size-sm);
}

.cwc-errors__meta-row {
  display: contents;
}

.cwc-errors__meta dt {
  color: var(--gl-fg-subtle);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cwc-errors__meta dd {
  margin: 0;
  word-break: break-word;
}

.cwc-errors__retry {
  margin-top: var(--gl-spacing-3);
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-2);
}

.cwc-errors__retry-btn {
  appearance: none;
  background: var(--gl-bg-surface-subtle);
  color: var(--gl-fg-default);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  cursor: pointer;
  font: inherit;
}

.cwc-errors__retry-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
